import { AnimatedSkeletonList } from "@/components/common/AnimatedSkeletonList/AnimatedSkeletonList"
import { Button } from "@/components/common/Button/Button"
import { toast } from "@/components/common/Toast"
import { Typography } from "@/components/common/Typography/Typography"
import { ActionSidebarRight } from "@/components/feature/ActionSidebarRight/ActionSidebarRight"
import { AnswerItem } from "@/components/feature/CollapsibleQuestionnaireList/AnswerItem/AnswerItem"
import { CollapsibleQuestionnaireList } from "@/components/feature/CollapsibleQuestionnaireList/CollapsibleQuestionnaireList"
import { ContinueLaterModal } from "@/components/feature/Modals/ContinueLaterModal/ContinueLaterModal"
import { DeleteQuestionnaireModal } from "@/components/feature/Modals/DeleteQuestionnaireModal/DeleteQuestionnaireModal"
import { MainLayout } from "@/components/layout/MainLayout/MainLayout"
import { useFeatureFlag } from "@/contexts/FeatureFlagContext"
import { useQuestionnaireStatusNavigation } from "@/hooks/useQuestionnaireStatusNavigation/useQuestionnaireStatusNavigation"
import { useScrollToAnswerByQuery } from "@/hooks/useScrollToAnswerByQuery/useScrollToAnswerByQuery"
import { useTextAreaStates } from "@/hooks/useTextAreaStates/useTextAreaStates"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { ErrorPage } from "@/pages/ErrorPage"
import { isQuestionnaireNotFound } from "@/services/api/questionnaireQueryPolicy"
import {
  useEditResponsePair,
  useGetQuestionnaireData,
  useUpdateQuestionnaireStatus,
} from "@/services/api/questionnaireService"
import { EditResponsePairKey } from "@/types/hooks/useTextAreaStates"
import { QuestionnaireStatus } from "@/types/questionnaire/models"
import { classNames } from "@/utils/classNames"
import { isExcelFileName, isPdfFileName } from "@/utils/fileUtils"
import { isLimitError } from "@/utils/limitErrors"
import { reduceNumTotalQuestions } from "@/utils/questionnaire/reduceQuestions"
import { IconCheck } from "@tabler/icons-react"
import { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router"

const EDIT_RESPONSE_PAIR_KEY: EditResponsePairKey = "response"

export const AnswersPage: React.FC = () => {
  const { translate } = useTranslation()
  const excelIntelligenceEnabled = useFeatureFlag("EXCEL_INTELLIGENCE")
  const pdfIntelligenceEnabled = useFeatureFlag("PDF_INTELLIGENCE")
  const { questionnaireId } = useParams<Record<string, string | undefined>>()
  const navigate = useNavigate()

  const [isContinueLaterModalOpen, setIsContinueLaterModalOpen] =
    useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)

  const {
    data: questionnaire,
    refetch: refetchQuestionnaire,
    isPending: isQuestionnairePending,
    isFetched: isQuestionnaireFetched,
    isError: isQuestionnaireError,
    error: questionnaireError,
  } = useGetQuestionnaireData(questionnaireId)

  const {
    mutate: updateQuestionnaireStatus,
    isSuccess: isUpdateStatusSuccess,
    isError: isUpdateStatusError,
    error: updateStatusError,
    data: updateStatusResponse,
    isPending: isUpdateStatusPending,
  } = useUpdateQuestionnaireStatus()

  const [numTotalQuestions, setNumTotalQuestions] = useState(0)

  const [, setQuestionnaireStatus] =
    useQuestionnaireStatusNavigation(questionnaire)
  useScrollToAnswerByQuery()

  useEffect(() => {
    if (isQuestionnaireError && !isQuestionnaireNotFound(questionnaireError)) {
      toast.error(translate("QUESTIONNAIRE_QUESTIONS/TOAST_GET_ERROR"))
    } else if (isQuestionnaireFetched && questionnaire) {
      setNumTotalQuestions(reduceNumTotalQuestions(questionnaire))
    }
  }, [isQuestionnaireFetched, questionnaire, isQuestionnaireError, translate])

  useEffect(() => {
    const handleQuestionnaireUpdate = async () => {
      if (isUpdateStatusError) {
        // The axios interceptor already showed the limit toast with its upgrade action
        if (!isLimitError(updateStatusError)) {
          toast.error(translate("QUESTIONNAIRE_ANSWERS/STATUS_UPDATE_ERROR"))
        }
      } else if (isUpdateStatusSuccess && updateStatusResponse) {
        await refetchQuestionnaire()
        setQuestionnaireStatus(updateStatusResponse.status)
      }
    }
    handleQuestionnaireUpdate()
  }, [
    isUpdateStatusSuccess,
    updateStatusResponse,
    isUpdateStatusError,
    updateStatusError,
    setQuestionnaireStatus,
    refetchQuestionnaire,
    translate,
  ])

  const handleOpenContinueLaterModal = () => {
    setIsContinueLaterModalOpen(true)
  }

  const handleOpenDeleteModal = () => {
    setIsDeleteModalOpen(true)
  }

  const handleCompletedButtonClick = () => {
    updateQuestionnaireStatus({
      questionnaireId,
      status: QuestionnaireStatus.Completed,
    })
  }

  const handleSwitchView = () => {
    if (!questionnaire) return
    if (isExcelFileName(questionnaire.fileName)) {
      navigate(`/questionnaire/${questionnaireId}/excel-answers`, {
        replace: true,
      })
    } else if (isPdfFileName(questionnaire.fileName)) {
      navigate(`/questionnaire/${questionnaireId}/pdf-answers`, {
        replace: true,
      })
    } else {
      toast.error(
        translate("QUESTIONNAIRE_ANSWERS/TOAST_FILETYPE_NOT_SUPPORTED"),
      )
    }
  }

  const { mutate: mutateResponsePair } = useEditResponsePair()
  const [{ textsCount }, updateTextAreaState] = useTextAreaStates(
    questionnaire,
    mutateResponsePair,
    EDIT_RESPONSE_PAIR_KEY,
  )

  if (!questionnaireId) {
    toast.error(translate("QUESTIONNAIRE_ANSWERS/TOAST_ID_NOT_FOUND"))
    return null
  }

  let contentCenter = <></>
  if (isQuestionnairePending) {
    contentCenter = (
      <>
        <Typography as="h1" variant="titleL" className="pb-L">
          {translate("QUESTIONNAIRE_ANSWERS/PAGE_TITLE")}
        </Typography>
        <AnimatedSkeletonList />
      </>
    )
  } else if (isQuestionnaireFetched && questionnaire) {
    contentCenter = (
      <div>
        <div
          className={classNames("flex items-center justify-between", "pb-L")}
        >
          <Typography as="h1" variant="titleL">
            {translate("QUESTIONNAIRE_ANSWERS/PAGE_TITLE")}
          </Typography>
          <Typography as="p" variant="textM">
            {translate("QUESTIONNAIRE_ANSWERS/COUNT_TOTAL_ANSWERS")}{" "}
            <Typography
              as="span"
              variant="textM"
              className="font-bold text-yellow1"
            >
              {textsCount.total}
            </Typography>
            <Typography
              as="span"
              variant="textM"
              className="font-bold text-white"
            >
              /{numTotalQuestions}
            </Typography>
          </Typography>
        </div>

        {questionnaire?.sections.map((section, index) => {
          return (
            <CollapsibleQuestionnaireList
              key={`${section.id}-${index}`}
              sectionIndex={index}
              sectionTitle={section.name}
              sectionCountDisplay={
                <Typography variant="textM">
                  <Typography
                    as="span"
                    variant="textM"
                    className="text-yellow1"
                  >
                    {textsCount.sections[section.id] || 0}
                  </Typography>
                  {"/"}
                  <Typography as="span" variant="textM">
                    {section.responsePairs.length}
                  </Typography>
                </Typography>
              }
            >
              {section.responsePairs.map((pair, index) => {
                return (
                  <AnswerItem
                    key={`${pair.id}-${index}`}
                    responsePair={pair}
                    sectionId={section.id}
                    updateText={updateTextAreaState}
                  />
                )
              })}
            </CollapsibleQuestionnaireList>
          )
        })}
      </div>
    )
  }

  if (isQuestionnaireNotFound(questionnaireError))
    return <ErrorPage variant="not-found" withLayout />

  return (
    <MainLayout
      contentCenter={
        <>
          {isContinueLaterModalOpen && (
            <ContinueLaterModal
              setIsContinueLaterModalOpen={setIsContinueLaterModalOpen}
            />
          )}

          {isDeleteModalOpen && (
            <DeleteQuestionnaireModal
              questionnaireId={questionnaireId}
              setIsDeleteModalOpen={setIsDeleteModalOpen}
            />
          )}
          {contentCenter}
        </>
      }
      contentRight={
        questionnaire && (
          <ActionSidebarRight
            questionnaire={questionnaire}
            handleOpenDeleteModal={handleOpenDeleteModal}
            isLoading={isUpdateStatusPending}
            actionButtons={
              <>
                <Button
                  variantColor="yellow"
                  title={translate("QUESTIONNAIRE_ANSWERS/DONE")}
                  label={translate("QUESTIONNAIRE_ANSWERS/DONE")}
                  onClick={handleCompletedButtonClick}
                  variantSize="medium"
                  isFullWidth={true}
                  IconRight={IconCheck}
                  isDisabled={isUpdateStatusPending}
                />
                <Button
                  variantColor="dark"
                  title={translate("QUESTIONNAIRE/CONTINUE_LATER")}
                  label={translate("QUESTIONNAIRE/CONTINUE_LATER")}
                  onClick={handleOpenContinueLaterModal}
                  variantSize="medium"
                  isFullWidth={true}
                />
                {questionnaire.hasPositionData &&
                (excelIntelligenceEnabled || pdfIntelligenceEnabled) ? (
                  <Button
                    variantColor="dark"
                    title={translate("QUESTIONNAIRE_ANSWERS/SWITCH_VIEW")}
                    label={translate("QUESTIONNAIRE_ANSWERS/SWITCH_VIEW")}
                    onClick={handleSwitchView}
                    variantSize="medium"
                    isFullWidth={true}
                  />
                ) : null}
              </>
            }
          />
        )
      }
    ></MainLayout>
  )
}
